// Hair recolour pass (spike) for the photo-based VTO pipeline.
//
// A coarse hair region already exists: the ONNX face-parsing model
// (CelebAMask-HQ / BiSeNet) segments hair, exposed as `semanticMap.masks.hair`.
// But that region mask can't separate a strand from the background behind it,
// so this pass refines it into a per-pixel coverage matte (see hairMatte) before
// tinting — keeping the recolour off skin, off the background, and out of the
// gaps in thin hair.
//
// Technique: composite a solid target colour onto the hair region with the
// Canvas2D `color` blend mode, which takes HUE + SATURATION from the source
// and keeps LUMINANCE from the underlying photo. That preserves every strand,
// highlight and shadow, so the result reads as recoloured hair rather than a
// flat sticker.
//
// Known limit (inherent, not a bug): `color` preserves luminance, so it CANNOT
// lighten — dark hair won't become realistic blonde (no highlight detail to
// recover). Works well for: darkening, warm/cool shifts, auburn/red/copper,
// vivid fashion colours, and any recolour that stays at or below the hair's
// current lightness.

import type { SemanticFaceMap } from '../semantic/semanticFaceMap';
import { combineMasks, growMask, scaleMask, shrinkMask, softenMask, subtractMasks } from './maskUtils';
import { buildHairCoverageMatte } from './hairMatte';
import { buildHairAlphaCoverage, type PersonAlpha } from './hairAlphaMatte';

export interface HairOptions {
  hex: string;                 // target hair colour, e.g. "#7a3b1f"
  semanticMap: SemanticFaceMap | null | undefined;
  strength?: number;           // 0..1 overall intensity (default 0.85)
  // ML portrait alpha-matte (MODNet). When present, coverage = personAlpha ×
  // envelope — strand-accurate, gaps read background, grey hair retained. Falls
  // back to the heuristic coverage matte when absent.
  personAlpha?: PersonAlpha;
}

export interface HairRenderResult {
  canvas: HTMLCanvasElement;
  applied: boolean;            // false when no hair mask was available
}

export function renderHairOverlay(
  source: HTMLCanvasElement | ImageBitmap,
  options: HairOptions
): HairRenderResult {
  const W = (source as HTMLCanvasElement).width;
  const H = (source as HTMLCanvasElement).height;
  const out = document.createElement('canvas');
  out.width = W;
  out.height = H;
  const ctx = out.getContext('2d');
  if (!ctx) throw new Error('2D context unavailable');

  // Layer 0: the original photo.
  ctx.drawImage(source, 0, 0, W, H);

  const hairMask = options.semanticMap?.masks?.hair;
  if (!hairMask) return { canvas: out, applied: false };

  // The exposed hair mask is the coarse PROTECTION mask (grown + feathered to
  // over-cover the hairline). Two failure modes when reused verbatim to *paint*
  // hair through the `color` blend: (a) the grown rim tints forehead skin and
  // the background → a halo; (b) in thin/wispy hair the mask covers the gaps
  // between strands, so the background showing through gets tinted too.
  //
  // Both are the same root cause — a region mask can't tell a strand from the
  // background behind it. So instead of a region mask we derive a per-pixel hair
  // COVERAGE matte from the photo (see hairMatte): the colour behind the hair is
  // inpainted, and each pixel's deviation from that backdrop gives its opacity.
  // Gaps (≈ backdrop) and skin leaks → ~0 opacity (untouched); strands → full.
  const mask = scaleMask(hairMask, W, H);
  const minDim = Math.min(W, H);
  // Hole: binary, generously grown past the hairline so every wisp is inside it
  // → the backdrop is inpainted only from genuine non-hair pixels (not wisps).
  const hole = growMask(mask, Math.max(6, minDim * 0.025));
  // Envelope: feathered bound on where tint may land. ERODED inward — never past
  // the coarse hairline. A grown envelope re-introduces the halo: through the
  // `color` blend the tint catches the bright hair→background transition pixels
  // and glows a "corona" around the head. Eroding keeps tint strictly inside the
  // hair; the outermost wisps stay their natural colour (no halo, no skin cast).
  const envelope = softenMask(shrinkMask(mask, Math.max(6, minDim * 0.022)), Math.max(1, minDim * 0.004));
  // Core: definite solid hair (more strongly eroded) — coverage is floored to 1.
  const core = shrinkMask(mask, Math.max(5, minDim * 0.026));
  // ML alpha-matte path (preferred): coverage = personAlpha × region. The
  // person alpha resolves strands/gaps and keeps low-contrast hair, replacing
  // the heuristic core's blind gap-tinting. It also already rejects the
  // background, so the region only needs a LIGHT erosion (just enough to stay
  // off the face/shirt at the hairline) — far less than the heuristic envelope,
  // which is heavily eroded to suppress its own halo. The lighter region lets
  // the alpha colour fine edge wisps and recover coverage on low-contrast hair.
  // Protection mask the recolour must stay off: full skin (incl. neck) + ears +
  // cloth. The person-alpha can't tell hair from skin where they overlap (both
  // foreground), so a cool shade (blue-black, platinum) would cast a dirty grey
  // on warm skin at the hairline / ears / neck. Subtracting it keeps tint off.
  //
  // Use faceSkinFull, NOT `skin`: the hair mask is grown to over-reach the
  // hairline (it was built to protect makeup), and `skin` (cosmeticSkin) has
  // that same hair region carved out AND is clipped to the face oval — so
  // subtracting it was a no-op in the overreach band (tint bled on forehead /
  // temple / cheek) and left the neck/jaw unprotected. faceSkinFull keeps the
  // true skin boundary, incl. neck, while real strands (labelled hair) still tint.
  //
  // Grow the union slightly, THEN soften, before subtracting. The grow claws
  // back the fine flyaway strands that sit DIRECTLY against skin/ear (worst at
  // the ear and sideburn on short wispy hair, e.g. demo 50: ONNX labels those
  // wisps `hair` and person-alpha keeps them, so the `color` blend smears a pink
  // veil over the skin showing between them). The main hair mass sits further
  // from skin and stays tinted; only the immediate skin-adjacent fringe is
  // reclaimed — which also reads as a cleaner, tighter hairline. The soften then
  // makes the recolour FADE into skin instead of stopping on a hard one-sided
  // ONNX seam along the cheek / jaw.
  const skinGuard =
    options.semanticMap?.masks?.faceSkinFull ?? options.semanticMap?.masks?.skin;
  const protection = softenMask(
    growMask(
      combineMasks(W, H, [
        skinGuard,
        options.semanticMap?.masks?.ears,
        options.semanticMap?.masks?.cloth
      ]),
      Math.max(2, minDim * 0.006)
    ),
    Math.max(1.5, minDim * 0.004)
  );
  const coverage = options.personAlpha
    ? buildHairAlphaCoverage(
        out,
        W,
        H,
        options.personAlpha,
        subtractMasks(
          softenMask(shrinkMask(mask, Math.max(3, minDim * 0.008)), Math.max(1, minDim * 0.004)),
          [protection]
        )
      )
    : buildHairCoverageMatte(out, W, H, { hole, envelope, core });

  // Build the recolour layer: solid target colour clipped to the coverage matte.
  const layer = document.createElement('canvas');
  layer.width = W;
  layer.height = H;
  const lctx = layer.getContext('2d');
  if (!lctx) return { canvas: out, applied: false };

  lctx.fillStyle = options.hex;
  lctx.fillRect(0, 0, W, H);
  lctx.globalCompositeOperation = 'destination-in';
  lctx.drawImage(coverage, 0, 0, W, H);
  lctx.globalCompositeOperation = 'source-over';

  // Composite onto the photo. `color` = hue+saturation from the layer,
  // luminance from the base → strand detail survives. Strength gives a knob
  // to dial the effect back toward a natural tint.
  const strength = Math.max(0, Math.min(1, options.strength ?? 0.85));
  ctx.save();
  ctx.globalAlpha = strength;
  ctx.globalCompositeOperation = 'color';
  ctx.drawImage(layer, 0, 0);
  ctx.restore();

  return { canvas: out, applied: true };
}
